import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchAgentModels, fetchAgentModes, updateAgentSettings } from '../../../helpers/agents';

export default function useModelAndMode({ selectedAgentId, agentDetailMode }) {
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(null);

  const [availableModes, setAvailableModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState('agent');

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const response = await fetchAgentModels();
      const models = response.data || [];
      setAvailableModels(models);
      const defaultModel = models.find((m) => m.is_default && m.is_active);
      const firstActive = models.find((m) => m.is_active);
      if (defaultModel) {
        setSelectedModelId(defaultModel.id);
      } else if (firstActive) {
        setSelectedModelId(firstActive.id);
      }
    } catch (e) {
      console.error('Failed to load models:', e);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const loadModes = useCallback(async () => {
    setModesLoading(true);
    try {
      const response = await fetchAgentModes();
      const modes = response.data?.modes || [];
      setAvailableModes(modes);
    } catch (e) {
      console.error('Failed to load modes:', e);
    } finally {
      setModesLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);
  useEffect(() => { loadModes(); }, [loadModes]);

  // Sync selectedMode when a loaded agent detail has a different mode
  useEffect(() => {
    if (agentDetailMode) {
      setSelectedMode(agentDetailMode);
    }
  }, [agentDetailMode]);

  const handleModeChange = useCallback(async (mode) => {
    setSelectedMode(mode);
    if (selectedAgentId) {
      try {
        await updateAgentSettings(selectedAgentId, { mode });
      } catch (e) {
        console.error('Failed to update mode:', e);
        toast.error('Failed to switch mode');
      }
    }
  }, [selectedAgentId]);

  return {
    availableModels,
    modelsLoading,
    selectedModelId,
    setSelectedModelId,
    availableModes,
    modesLoading,
    selectedMode,
    setSelectedMode,
    handleModeChange,
  };
}
